import { useHealth } from '@smarthome/api';
import { useEffect, useRef, useCallback } from 'react';

const VERSION_CHECK_INTERVAL = 60_000; // Check every 60 seconds

/**
 * Hook that monitors the backend version and triggers a reload when a new version is detected.
 *
 * The version is checked on initial load and then periodically. When a new version is detected,
 * the page is automatically reloaded to ensure the user has the latest frontend code.
 */
export function useVersionCheck() {
  const initialVersionRef = useRef<string | null>(null);

  const { data: healthData } = useHealth({
    query: {
      refetchInterval: VERSION_CHECK_INTERVAL,
      refetchIntervalInBackground: true,
      staleTime: VERSION_CHECK_INTERVAL - 5000, // Consider stale slightly before next check
    },
  });

  useEffect(() => {
    if (!healthData?.version) return;

    // Store the initial version on first load
    if (initialVersionRef.current === null) {
      initialVersionRef.current = healthData.version;
      return;
    }

    // If version has changed, reload the page
    if (healthData.version !== initialVersionRef.current) {
      window.location.reload();
    }
  }, [healthData?.version]);

  return {
    version: healthData?.version ?? null,
  };
}

/**
 * Hook that provides a reload function for manual page refresh.
 * Useful for PWA mode where the user cannot easily refresh the page.
 */
export function useReload() {
  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { reload };
}
